import { useState } from "react";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { EditProfileSheet } from "./sheets/EditProfileSheet";
import { getInitialsFromEmployee } from "@/utils/utils";
import { Body, Heading } from "@hypercard/web-components";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Employee } from "@common/utils/graphql";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { AttributeKeyValueCell } from "@common/components";
import { AddDelegateInput } from "./AddDelegateInput";
import { useCurrentUser, useFeatureFlags } from "@common/hooks";
import { DelegateTransactionsVisibility } from "./DelegateTransactionsVisibility";

export const ProfileTab = () => {
  const userStore = useUserStore.getState();
  const { isDelegateUsersEnabled } = useFeatureFlags();
  const [editProfile, setEditProfile] = useState<boolean>(false);

  const { maybeCurrentUser, currentUserLoading } = useCurrentUser();

  const managerName = !maybeCurrentUser?.employee?.manager
    ? "No manager"
    : `${maybeCurrentUser?.employee?.manager?.firstName} ${maybeCurrentUser?.employee?.manager?.lastName}`;

  const onClose = () => {
    setEditProfile(false);
  };

  if (currentUserLoading && !maybeCurrentUser) {
    return <FullPageLoader />;
  }

  const isCardholder = !!maybeCurrentUser?.cardholder;

  const employeeSinceYear = maybeCurrentUser?.employee?.employmentStartDate
    ? new Date(maybeCurrentUser?.employee?.employmentStartDate).getFullYear()
    : null;
  const employeeSinceText = employeeSinceYear
    ? `Employee since ${employeeSinceYear}`
    : null;

  if (!editProfile) {
    return (
      <div className="mt-8 flex flex-col">
        <div className="flex flex-row items-center space-x-4">
          {/* Avatar */}
          <Avatar className="h-16 w-16">
            <AvatarFallback>
              <Body size="lg" weight="medium">
                {getInitialsFromEmployee(userStore as Employee)}
              </Body>
            </AvatarFallback>
          </Avatar>

          {/* Name and employee since */}
          <div className="flex-1 space-y-1">
            <Heading size="lg" weight="regular" className="font-sans">
              {userStore.displayName}
            </Heading>
            {employeeSinceText && (
              <Body size="sm" weight="regular">
                {employeeSinceText}
              </Body>
            )}
          </div>
        </div>

        {/* Profile attributes grid */}
        <div className="mt-12">
          <div className="inline-grid grid-cols-[auto_auto_auto_auto] gap-8">
            {/* Row 1: work email, personal email, phone number */}
            <AttributeKeyValueCell
              keyName="Work Email"
              value={userStore.workEmail}
            />
            {isCardholder ? (
              <AttributeKeyValueCell
                keyName="Personal Email"
                value={maybeCurrentUser.cardholder!.personalEmail}
              />
            ) : (
              <div />
            )}
            {isCardholder ? (
              <AttributeKeyValueCell
                keyName="Phone Number"
                value={maybeCurrentUser.cardholder!.phoneNumber}
              />
            ) : (
              <div />
            )}
            <div />

            {/* Row 2: role, department, manager, location */}
            <AttributeKeyValueCell
              keyName="Role"
              value={maybeCurrentUser?.employeeDisplayRole}
            />
            <AttributeKeyValueCell
              keyName="Department"
              value={maybeCurrentUser?.employee?.department.name}
            />
            <AttributeKeyValueCell keyName="Manager" value={managerName} />
            {isCardholder && maybeCurrentUser.cardholder!.residentialAddress ? (
              <AttributeKeyValueCell
                keyName="Location"
                value={maybeCurrentUser.cardholder!.residentialAddress.city}
              />
            ) : (
              <div />
            )}

            {/* Row 3: delegate, delegate view control */}
            {isDelegateUsersEnabled && (
              <AttributeKeyValueCell
                keyName="Delegate"
                value={<AddDelegateInput />}
              />
            )}
          </div>
        </div>

        {/* Toggle delegate visibility */}
        {/* Will have opacity 50 if user has no delegates */}
        {isDelegateUsersEnabled && <DelegateTransactionsVisibility />}
      </div>
    );
  }

  if (editProfile) {
    return (
      <>
        <EditProfileSheet isOpen={editProfile} onClose={onClose} />
      </>
    );
  }
};
